/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */
import { AlertTriangle } from 'lucide-react';

import TooltipOverParent from '../sessionRecorder/TooltipOverParent';
import Spinner from '../ui/Spinner';

import { OUTPUT, RESULT } from '@/commons/data';
import { RunStatus } from '@/commons/protocols';
import {
    LiveAgentStatusCanceled,
    LiveAgentStatusCheck,
    LiveAgentStatusDisabled,
    LiveAgentStatusDismissed,
    LiveAgentStatusHelp,
    LiveAgentStatusMaintenance,
    LiveAgentStatusPending,
    LiveAgentStatusWarning,
} from '@/pages/projects/LiveAgentStatusIcons';
import { cn } from '@/utils';

type StatusIconProps = {
    status?: RunStatus;
    result?: RESULT | null;
    output?: OUTPUT | null;
    size?: number;
    spinnerSize?: number | string;
    helpText?: string;
    className?: string;
    maintenance?: boolean;
    disabled?: boolean;
};

export const StatusIcon = ({
    status,
    result,
    output,
    size,
    spinnerSize,
    helpText,
    className,
    maintenance,
    disabled,
}: StatusIconProps) => {
    const SIZE = size ?? 20;
    // eslint-disable-next-line react/self-closing-comp
    let icon = <div></div>;
    let defaultHelpText = '';
    const iconStatus = status ?? 'finished';

    if (disabled) {
        icon = <LiveAgentStatusDisabled size={SIZE} className={className} />;
        defaultHelpText = 'Story disabled';
    } else if (output === 'PASS') {
        icon = <LiveAgentStatusCheck size={SIZE} className={className} />;
        defaultHelpText = 'Run passed. No bug was detected.';
    } else if (output === 'FAIL') {
        icon = <LiveAgentStatusWarning size={SIZE} className={className} />;
        defaultHelpText = 'Heal detected a bug in this run.';
    } else if (output === 'PENDING') {
        icon = <LiveAgentStatusPending size={SIZE} className={className} />;
        defaultHelpText =
            "Run under review. Heal will review the run shortly and determine if it's an agent crash or a bug.";
    } else if (output === 'DISMISSED') {
        if (maintenance) {
            icon = <LiveAgentStatusMaintenance size={SIZE} className={className} />;
            defaultHelpText = 'Story under maintenance';
        } else {
            icon = <LiveAgentStatusDismissed size={SIZE} className={className} />;
            defaultHelpText = 'Run dismissed';
        }
    } else if (result === 'PASS') {
        icon = <LiveAgentStatusCheck size={SIZE} className={className} />;
        defaultHelpText = 'Test passed';
    } else if (result === 'FAIL') {
        icon = <LiveAgentStatusWarning size={SIZE} className={className} />;
        defaultHelpText = 'Test failed: it is likely there is a bug';
    } else if (result === 'CRASH') {
        icon = <LiveAgentStatusHelp size={SIZE} className={className} />;
        defaultHelpText = 'The agent needs user input to complete the test.';
    } else if (iconStatus === 'running' && result === 'UNKNOWN') {
        icon = <Spinner size={spinnerSize} />;
        defaultHelpText = 'Queued to run';
    } else if (iconStatus === 'queued' && result === 'UNKNOWN') {
        icon = <Spinner size={spinnerSize} />;
        defaultHelpText = 'Queued to run';
    } else if (iconStatus === 'finished' && (result === undefined || result === 'UNKNOWN' || result === 'CANCELLED')) {
        icon = <LiveAgentStatusCanceled size={SIZE} className={className} />;
        defaultHelpText = 'The step was not executed.';
    } else if (iconStatus === 'running') {
        icon = <Spinner size={spinnerSize} />;
        defaultHelpText = 'The test run is running';
    } else if (iconStatus === 'queued') {
        icon = (
            <div className={cn('flex items-center justify-center', `h-${spinnerSize} w-${spinnerSize}`)}>
                <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gray-500 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-gray-300" />
                </span>
            </div>
        );
        defaultHelpText = 'The test run is queued';
    } else if (iconStatus === 'failed') {
        icon = <AlertTriangle size={SIZE} color="gray" className={className} />;
        defaultHelpText =
            'The run crashed with status `failed`, without a result. This is likely due to a runner issue.';
    } else if (iconStatus === 'canceled') {
        icon = <LiveAgentStatusCanceled size={SIZE} className={className} />;
        defaultHelpText = 'The run was canceled by a user.';
    }

    return (
        <TooltipOverParent
            renderTooltip={() => <p>{helpText ?? defaultHelpText}</p>}
            renderTrigger={() => <div className="inline z-50">{icon}</div>}
        />
    );
};
